import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import './Dashboard.css';
import { PackageIcon, DollarIcon, ClockIcon, CheckCircleIcon, ShoppingCartIcon, PlusCircleIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        fetch('/api/orders/stats/summary', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/orders?limit=5', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setRecentOrders(ordersData.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const locale = language === 'ar' ? 'ar-AE' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => <AedPrice amount={amount} />;

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'warning',
      processing: 'primary',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const getStatusText = (status) => {
    const statusTranslations = {
      pending: t('pending'),
      processing: t('processing'),
      shipped: t('shipped'),
      delivered: t('delivered'),
      cancelled: t('cancelled')
    };
    return statusTranslations[status] || status;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4" className="active">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{t('products')}</Link>
            <Link to="/dashboard4/orders">{t('orders')}</Link>
            <Link to="/dashboard4/create-order">{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{language === "ar" ? "المخزون" : "Inventory"}</Link>
            <Link to="/dashboard4/stats">{language === "ar" ? "الإحصائيات" : "Stats"}</Link>
            <Link to="/dashboard4/settings">{language === "ar" ? "الإعدادات" : "Settings"}</Link>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon"><PackageIcon size="24px" /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.totalOrders}</span>
              <span className="stat-label">{t('totalOrders')}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon"><span style={{ display: 'inline-block', width: '24px', height: '24px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/uploads/icons/aed.svg)', WebkitMaskSize: 'contain', WebkitMaskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskImage: 'url(/uploads/icons/aed.svg)', maskSize: 'contain', maskRepeat: 'no-repeat', maskPosition: 'center' }} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.totalRevenue.toLocaleString(language === 'ar' ? 'ar-AE' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
              <span className="stat-label">{language === 'ar' ? 'AED الإيرادات' : 'AED Revenue'}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon"><ClockIcon size="24px" /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.pendingOrders}</span>
              <span className="stat-label">{t('pendingOrders')}</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon"><CheckCircleIcon size="24px" /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.completedOrders}</span>
              <span className="stat-label">{t('completedOrders')}</span>
            </div>
          </div>
        </div>

        <div className="admin-grid">
          <div className="admin-card">
            <div className="card-header">
              <h3>{t('recentOrders')}</h3>
              <Link to="/dashboard4/orders" className="view-all">
                {t('viewAll')} {isRTL ? '←' : '→'}
              </Link>
            </div>

            {recentOrders.length === 0 ? (
              <p className="empty-text">{t('noOrders')}</p>
            ) : (
              <>
              {/* Desktop Table */}
              <div className="orders-table desktop-only">
                <table>
                  <thead>
                    <tr>
                      <th>{language === 'ar' ? 'رقم الطلب' : 'Order ID'}</th>
                      <th>{language === 'ar' ? 'العميل' : 'Customer'}</th>
                      <th>{language === 'ar' ? 'التاريخ' : 'Date'}</th>
                      <th>{t('total')}</th>
                      <th>{language === 'ar' ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map(order => (
                      <tr key={order.id}>
                        <td>#{order.id.slice(0, 8)}</td>
                        <td>{order.customerName}</td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>{formatCurrency(order.total)}</td>
                        <td>
                          <span className={`badge badge-${getStatusBadge(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="recent-orders-mobile mobile-only">
                {recentOrders.map(order => (
                  <Link key={order.id} to={`/dashboard4/orders/${order.id}`} className="recent-order-card">
                    <div className="recent-order-info">
                      <span className="recent-order-id">#{order.id}</span>
                      <span className="recent-order-customer">{order.customerName}</span>
                    </div>
                    <div className="recent-order-meta">
                      <div className="recent-order-total">{formatCurrency(order.total)}</div>
                      <div className="recent-order-date">{formatDate(order.createdAt)}</div>
                    </div>
                  </Link>
                ))}
              </div>
              </>
            )}
          </div>

          <div className="admin-card">
            <div className="card-header">
              <h3>{t('quickActions')}</h3>
            </div>
            <div className="quick-actions">
              <Link to="/dashboard4/products" className="action-btn">
                <PackageIcon size="22px" />
                <span>{t('manageProducts')}</span>
              </Link>
              <Link to="/dashboard4/orders" className="action-btn">
                <ShoppingCartIcon size="22px" />
                <span>{t('viewOrders')}</span>
              </Link>
              <Link to="/dashboard4/create-order" className="action-btn">
                <PlusCircleIcon size="22px" />
                <span>{language === 'ar' ? 'إنشاء طلب' : 'Create Order'}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
